import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';

const FIXED_COST_CATEGORIES = ['SOFTWARE','ATTREZZATURA','STUDIO','ASSICURAZIONE','MARKETING','FORMAZIONE','ALTRO'] as const;
type FixedCostCategory = typeof FIXED_COST_CATEGORIES[number];

interface FixedCostItem {
  id: string; name: string; amount: number; category: FixedCostCategory; isActive: boolean; notes?: string | null;
}

const CATEGORY_LABELS: Record<FixedCostCategory, string> = {
  SOFTWARE: 'Software', ATTREZZATURA: 'Attrezzatura', STUDIO: 'Studio',
  ASSICURAZIONE: 'Assicurazione', MARKETING: 'Marketing', FORMAZIONE: 'Formazione', ALTRO: 'Altro',
};

const CATEGORY_COLORS: Record<FixedCostCategory, string> = {
  SOFTWARE: 'var(--color-primary)',
  ATTREZZATURA: 'var(--color-gold)',
  STUDIO: '#7b6cf6',
  ASSICURAZIONE: 'var(--color-success)',
  MARKETING: 'var(--color-error)',
  FORMAZIONE: '#3498db',
  ALTRO: 'var(--color-text-faint)',
};

const inputStyle: React.CSSProperties = {
  background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
  fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none', width: '100%',
};

const iconBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-muted)', padding: '0.25rem',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
};

export function FixedCostsSection() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', amount: '', category: 'SOFTWARE' as FixedCostCategory, notes: '' });

  const { data: fcData } = useQuery<{ data: FixedCostItem[]; monthlyTotal: number; annualTotal: number }>({
    queryKey: ['fixed-costs'],
    queryFn: () => apiGet<{ data: FixedCostItem[]; monthlyTotal: number; annualTotal: number }>('/fixed-costs'),
  } as any);
  const costs = fcData?.data ?? [];
  const monthlyTotal = fcData?.monthlyTotal ?? 0;
  const annualTotal = fcData?.annualTotal ?? 0;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['fixed-costs'] });
  const resetForm = () => { setForm({ name: '', amount: '', category: 'SOFTWARE', notes: '' }); setEditId(null); setShowForm(false); };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = { name: form.name, amount: parseFloat(form.amount), category: form.category, notes: form.notes || null };
      return editId ? apiPut(`/fixed-costs/${editId}`, payload) : apiPost('/fixed-costs', payload);
    },
    onSuccess: () => { invalidate(); resetForm(); toast.success(editId ? 'Costo aggiornato' : 'Costo aggiunto'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/fixed-costs/${id}`),
    onSuccess: () => { invalidate(); toast.success('Costo eliminato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => apiPut(`/fixed-costs/${id}`, { isActive }),
    onSuccess: () => invalidate(),
  });

  const startEdit = (c: FixedCostItem) => {
    setForm({ name: c.name, amount: String(c.amount), category: c.category, notes: c.notes ?? '' });
    setEditId(c.id);
    setShowForm(true);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
        Inserisci i costi fissi ricorrenti del tuo studio (abbonamenti, attrezzatura, affitto, ecc.) per calcolare il break-even reale nei report finanziari.
      </p>

      {costs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {costs.map((c: FixedCostItem) => (
            <div key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: '0.625rem',
              background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)', padding: '0.625rem 0.875rem',
              opacity: c.isActive ? 1 : 0.5,
            }}>
              <span style={{ background: CATEGORY_COLORS[c.category as FixedCostCategory], color: '#fff', fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                {CATEGORY_LABELS[c.category as FixedCostCategory]}
              </span>
              <span style={{ flex: 1, fontSize: 'var(--text-sm)' }}>{c.name}</span>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text)', minWidth: 80, textAlign: 'right' }}>
                {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(c.amount)}/mese
              </span>
              <button onClick={() => toggleMutation.mutate({ id: c.id, isActive: !c.isActive })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.isActive ? 'var(--color-success)' : 'var(--color-text-faint)', fontSize: 12 }}>
                {c.isActive ? '●' : '○'}
              </button>
              <button onClick={() => startEdit(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><Pencil size={13} /></button>
              <button onClick={() => { if (confirm(`Eliminare "${c.name}"?`)) deleteMutation.mutate(c.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error)' }}><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <Input label="Nome" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Abbonamento Adobe" />
          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600 }}>Categoria</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value as FixedCostCategory }))}
                style={{ width: '100%', background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', marginTop: 4 }}>
                {FIXED_COST_CATEGORIES.map(cat => <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>)}
              </select>
            </div>
            <Input label="Importo mensile (€)" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
          </div>
          <Input label="Note (opzionale)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="" />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending} disabled={!form.name || !form.amount}>
              {editId ? 'Aggiorna' : 'Aggiungi'}
            </Button>
            <Button variant="ghost" onClick={resetForm}>Annulla</Button>
          </div>
        </div>
      ) : (
        <Button variant="ghost" icon={<Plus size={14} />} onClick={() => setShowForm(true)} style={{ alignSelf: 'flex-start' }}>
          Aggiungi costo fisso
        </Button>
      )}

      {costs.length > 0 && (
        <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '0.875rem 1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Totale costi fissi mensili</span>
            <span style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--color-text)' }}>
              {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(monthlyTotal)}<span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginLeft: 4 }}>/mese</span>
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>Totale annuale</span>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              = {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(annualTotal)}/anno
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
